// The overdue pop-up (Carla, 2026-10-21). "You have N overdue tasks": opened by itself once a day on
// My tasks, and any time from "Overdue (N)". Each row says what it is, whose (client, project), when
// it was ORIGINALLY due and how overdue it is, and offers Complete, Today, Tomorrow, Next week and
// Pick a date. Several can be selected and moved or completed together.
//
// A TEAM task or a CLIENT/PROJECT task asks why it is moving, one tap (the server refuses the move
// without one, Aidey Backend/lib/taskDueChanges reasonRequired); a solo person's own task may say
// why or Skip. "No longer needed" offers to close the task instead. A repeating task moves only the
// occurrence shown (the server writes an exception; the series is unchanged).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button, Checkbox, Chip, DateInput, Field, Input, Banner, Skeleton, ErrorState, StatusPill } from '../../ui';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { showErrorToast, showToast } from '../../utils/toast';
import { setOccurrenceState } from '../../utils/recurringEdit';
import {
  MOVE_REASONS, NOTE_MAX, fetchOverdue, rescheduleTasks, addDays, nextMonday, dueDay, movedTag,
} from '../../utils/taskDueChanges';
import './overdue.css';

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function whereLine(row) {
  return [row.client?.name, row.project?.name].filter(Boolean).join(' · ');
}

// The reason step: shown in place of the list while a move waits for its reason.
function ReasonStep({ pending, onPick, onSkip, onCloseInstead, onBack, busy }) {
  const [chosen, setChosen] = useState(null);
  const [note, setNote] = useState('');
  const required = pending.rows.some((r) => r.reasonRequired);
  const n = pending.rows.length;
  const target = pending.label;
  return (
    <div className="ov-reason" data-testid="overdue-reason-step">
      <p className="ov-reason__lead">
        <strong>{n === 1 ? `Why is "${pending.rows[0].title}"` : `Why are ${plural(n, 'task')}`} moving to {target}?</strong>
        <span className="tk-small">{required
          ? ' A task shared with a team, a client or a project needs a reason.'
          : ' Optional for your own tasks.'}</span>
      </p>
      <div className="ov-reason__chips" role="group" aria-label="Reason">
        {MOVE_REASONS.map((r) => (
          <Chip key={r.value} selected={chosen === r.value} disabled={busy} data-reason={r.value}
            onClick={() => {
              setChosen(r.value);
              if (r.value !== 'other' && r.value !== 'no_longer_needed') onPick(r.value, null);
            }}>
            {r.label}
          </Chip>
        ))}
      </div>
      {chosen === 'no_longer_needed' && (
        <div className="ov-reason__more" data-testid="overdue-no-longer-needed">
          <p className="tk-muted">If it is no longer needed, close it rather than move it.</p>
          <div className="ov-actions">
            <Button variant="confirm" onClick={onCloseInstead} disabled={busy}>{n === 1 ? 'Close it instead' : 'Close them instead'}</Button>
            <Button variant="secondary" onClick={() => onPick('no_longer_needed', null)} disabled={busy}>Move anyway</Button>
          </div>
        </div>
      )}
      {chosen === 'other' && (
        <div className="ov-reason__more">
          <Field label="In a few words" hint={`${note.length}/${NOTE_MAX}`}>
            <Input value={note} maxLength={NOTE_MAX} onChange={(e) => setNote(e.target.value)} data-testid="overdue-reason-note" />
          </Field>
          <div className="ov-actions">
            <Button variant="confirm" onClick={() => onPick('other', note.trim())} disabled={busy || !note.trim()}>Move</Button>
          </div>
        </div>
      )}
      <div className="ov-actions ov-actions--end">
        <Button variant="tertiary" onClick={onBack} disabled={busy}>Back</Button>
        {!required && <Button variant="secondary" onClick={onSkip} disabled={busy} data-testid="overdue-reason-skip">Skip</Button>}
      </div>
    </div>
  );
}

function OverdueRow({ row, selected, onSelect, onMove, onComplete, busy, today }) {
  const [picking, setPicking] = useState(false);
  const [date, setDate] = useState(addDays(today, 1));
  const where = whereLine(row);
  const tag = movedTag(row.moves);
  const originally = row.originalDue && row.due && String(row.originalDue).slice(0, 10) !== String(row.due).slice(0, 10)
    ? `Originally due ${dueDay(row.originalDue)}` : `Due ${dueDay(row.due)}`;
  return (
    <li className="ov-row" data-testid="overdue-row" data-task-id={row.taskId}>
      <div className="ov-row__head">
        <Checkbox checked={selected} onChange={(on) => onSelect(row, on)} label={<span className="tk-hidden">Select {row.title}</span>} />
        <div className="ov-row__text">
          <span className="ov-row__title">{row.title}</span>
          {where && <span className="ov-row__where">{where}</span>}
          <span className="ov-row__when">
            <span>{originally}</span>
            <StatusPill tone="warning">{row.overdueText}</StatusPill>
            {tag && <span className="ov-tag" data-testid="moved-tag">{tag}</span>}
            {row.repeats && <span className="tk-small">This occurrence only</span>}
          </span>
        </div>
      </div>
      {row.isBooking ? (
        <p className="tk-small ov-row__note">A booking moves with Reschedule, so the client is told.</p>
      ) : (
        <div className="ov-actions" role="group" aria-label={`Actions for ${row.title}`}>
          <Button size="sm" variant="confirm" disabled={busy} onClick={() => onComplete([row])}>Complete</Button>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => onMove([row], today, 'today')}>Today</Button>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => onMove([row], addDays(today, 1), 'tomorrow')}>Tomorrow</Button>
          <Button size="sm" variant="secondary" disabled={busy} onClick={() => onMove([row], nextMonday(today), 'next week')}>Next week</Button>
          <Button size="sm" variant="secondary" disabled={busy} aria-expanded={picking} onClick={() => setPicking((p) => !p)}>Pick a date</Button>
        </div>
      )}
      {picking && (
        <div className="ov-pick">
          <Field label="Move to"><DateInput value={date} min={today} onChange={(e) => setDate(e.target.value)} /></Field>
          <Button size="sm" variant="confirm" disabled={busy || !date} onClick={() => { setPicking(false); onMove([row], date, dueDay(date)); }}>Move</Button>
        </div>
      )}
    </li>
  );
}

export default function OverduePopup({ open, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [pending, setPending] = useState(null);   // { rows, to, label } waiting for a reason
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState([]);
  const [bulkPicking, setBulkPicking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await fetchOverdue();
      setData(d);
      setSelected((s) => new Set([...s].filter((id) => (d.tasks || []).some((r) => r.taskId === id))));
    } catch (e) {
      setError(messageFromError(e, 'Your overdue tasks could not be loaded.'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { setPending(null); setProblems([]); load(); } }, [open, load]);

  const rows = useMemo(() => data?.tasks || [], [data]);
  const today = data?.today || addDays(new Date().toISOString().slice(0, 10), 0);
  const [bulkDate, setBulkDate] = useState('');
  const chosenRows = rows.filter((r) => selected.has(r.taskId));
  const selectable = rows.filter((r) => !r.isBooking);

  const changed = () => { window.dispatchEvent(new Event('aidey:tasksUpdated')); onChanged?.(); };

  const runMove = async (pend, reason, note) => {
    setBusy(true); setProblems([]);
    try {
      const out = await rescheduleTasks({
        source: pend.rows.length > 1 ? 'bulk' : 'popup',
        moves: pend.rows.map((r) => ({ taskId: r.taskId, occurrenceStart: r.occurrenceStart || undefined, to: pend.to })),
        reason: reason || undefined, note: note || undefined,
      });
      const failed = (out.results || []).filter((r) => !r.ok);
      // Undo (item 7, decision 75): the server answers a handle only for a task no one else is affected by.
      const handles = (out.results || []).filter((r) => r.ok && r.undo).map((r) => r.undo);
      if (out.moved) showToast(`${plural(out.moved, 'task')} moved to ${pend.label}.`, handles.length ? { type: 'success', ms: 8000, action: { label: 'Undo', onClick: () => undoMoves(handles) } } : { type: 'success' });
      setProblems(failed.map((f) => ({ taskId: f.taskId, title: rows.find((r) => r.taskId === f.taskId)?.title, error: f.error })));
      setPending(null);
      setSelected(new Set());
      if (out.moved) changed();
      await load();
    } catch (e) {
      showErrorToast(messageFromError(e, 'Those tasks were not moved.'));
    } finally { setBusy(false); }
  };

  const complete = async (list) => {
    setBusy(true); setProblems([]);
    const failed = [];
    let done = 0;
    for (const r of list) {
      try {
        if (r.occurrenceStart) {
          const t = await setOccurrenceState({ seriesId: r.taskId, occurrence: { start: r.occurrenceStart }, updates: { completed: true } });
          if (!t) throw new Error('That occurrence was not marked done.');
        } else {
          await api(`/api/tasks/${r.taskId}`, { method: 'PUT', body: JSON.stringify({ completed: true }) });
        }
        done += 1;
      } catch (e) { failed.push({ taskId: r.taskId, title: r.title, error: messageFromError(e, 'Not marked done.') }); }
    }
    const doneRows = list.filter((r) => !failed.some((f) => f.taskId === r.taskId));
    if (done) { showToast(`${plural(done, 'task')} completed.`, { type: 'success', ms: 8000, action: { label: 'Undo', onClick: () => undoComplete(doneRows) } }); changed(); }
    setProblems(failed);
    setPending(null);
    setSelected(new Set());
    await load();
    setBusy(false);
  };

  // The Undo of a move: the task back exactly as it was, and the move off its history (the server's).
  const undoMoves = async (handles) => {
    try {
      await api('/api/tasks/reschedule/undo', { method: 'POST', body: JSON.stringify({ undo: handles }) });
      showToast('Moved back.', { type: 'success' });
    } catch (e) {
      const why = e?.body?.results?.find((r) => !r.ok)?.error;
      showErrorToast(why || messageFromError(e, 'That move was not undone.'));
    }
    changed(); await load();
  };
  const undoComplete = async (list) => {
    let failedOne = null;
    for (const r of list) {
      try {
        if (r.occurrenceStart) await setOccurrenceState({ seriesId: r.taskId, occurrence: { start: r.occurrenceStart }, updates: { completed: false } });
        else await api(`/api/tasks/${r.taskId}`, { method: 'PUT', body: JSON.stringify({ completed: false }) });
      } catch (e) { failedOne = failedOne || e; }
    }
    if (failedOne) showErrorToast(messageFromError(failedOne, 'It is still marked done.'));
    else showToast('Marked not done.', { type: 'success' });
    changed(); await load();
  };
  const askMove = (list, to, label) => setPending({ rows: list, to, label });
  const select = (row, on) => setSelected((s) => { const n = new Set(s); if (on) n.add(row.taskId); else n.delete(row.taskId); return n; });
  const allOn = selectable.length > 0 && selectable.every((r) => selected.has(r.taskId));

  const count = rows.length;
  const title = loading && !data ? 'Overdue tasks' : count ? `You have ${plural(count, 'overdue task')}` : 'Nothing overdue';

  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title={title} color="pastel-coral" width={760}
      footer={<Button variant="tertiary" onClick={onClose} disabled={busy}>Close</Button>}>
      <div className="ov" data-testid="overdue-popup">
        {error && <ErrorState message={error} onRetry={load} />}
        {!error && loading && !data && <Skeleton lines={4} label="Loading your overdue tasks" />}
        {problems.length > 0 && (
          <Banner tone="problem">
            {problems.map((p) => <div key={p.taskId}>{p.title ? `${p.title}: ` : ''}{p.error}</div>)}
          </Banner>
        )}
        {data && data.historyRead === false && (
          <Banner tone="notice">How often each task has moved could not be read just now, so each shows its current due date.</Banner>
        )}
        {pending ? (
          <ReasonStep pending={pending} busy={busy}
            onPick={(reason, note) => runMove(pending, reason, note)}
            onSkip={() => runMove(pending, null, null)}
            onCloseInstead={() => complete(pending.rows)}
            onBack={() => setPending(null)} />
        ) : data && (
          <>
            {count === 0 && <p className="tk-muted">Nothing is overdue. Well done.</p>}
            {count > 0 && (
              <div className="ov-bulk" data-testid="overdue-bulk">
                <Checkbox checked={allOn} indeterminate={!allOn && chosenRows.length > 0}
                  onChange={(on) => setSelected(on ? new Set(selectable.map((r) => r.taskId)) : new Set())}
                  label={chosenRows.length ? `${chosenRows.length} selected` : 'Select all'} />
                {chosenRows.length > 0 && (
                  <div className="ov-actions">
                    <Button size="sm" variant="secondary" disabled={busy} aria-expanded={bulkPicking} onClick={() => { setBulkDate(addDays(today, 1)); setBulkPicking((p) => !p); }}>Move to…</Button>
                    <Button size="sm" variant="confirm" disabled={busy} onClick={() => complete(chosenRows)}>Complete</Button>
                  </div>
                )}
              </div>
            )}
            {bulkPicking && chosenRows.length > 0 && (
              <div className="ov-pick ov-pick--bulk" data-testid="overdue-bulk-move">
                <div className="ov-actions">
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => { setBulkPicking(false); askMove(chosenRows, today, 'today'); }}>Today</Button>
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => { setBulkPicking(false); askMove(chosenRows, addDays(today, 1), 'tomorrow'); }}>Tomorrow</Button>
                  <Button size="sm" variant="secondary" disabled={busy} onClick={() => { setBulkPicking(false); askMove(chosenRows, nextMonday(today), 'next week'); }}>Next week</Button>
                </div>
                <Field label="Or a date"><DateInput value={bulkDate} min={today} onChange={(e) => setBulkDate(e.target.value)} /></Field>
                <Button size="sm" variant="confirm" disabled={busy || !bulkDate} onClick={() => { setBulkPicking(false); askMove(chosenRows, bulkDate, dueDay(bulkDate)); }}>Move</Button>
              </div>
            )}
            <ul className="ov-list">
              {rows.map((r) => (
                <OverdueRow key={`${r.taskId}:${r.occurrenceStart || ''}`} row={r} today={today} busy={busy}
                  selected={selected.has(r.taskId)} onSelect={select}
                  onMove={askMove} onComplete={complete} />
              ))}
            </ul>
          </>
        )}
      </div>
    </Modal>
  );
}
